import React, { useEffect, useRef, useState } from 'react';
import { FiHardDrive, FiPlus, FiTrash2, FiStar, FiInfo } from 'react-icons/fi';
import StorageBoxSidebarShared from './StorageBoxSidebarShared';
import StorageBoxStorageTermsModal from './StorageBoxStorageTermsModal';
import StorageBoxLockFolderNav from './StorageBoxLockFolderNav';
import { formatBytes } from '../api/storageBoxApi';
import { STORAGE_BOX_ROOT_LABEL } from '../constants';
import { useStorageBox } from '../StorageBoxContext';
import ItemTree from './ItemTree';
import StorageBoxSidebarLabels from './StorageBoxSidebarLabels';
import StorageBoxSidebarColorTags from './StorageBoxSidebarColorTags';
import { StorageBoxQuotaCardSkeleton } from '../../layout/ReusableComponent/Skeleton/RouteSkeletons';
import { StorageBoxSidebarDrawerProvider, useStorageBoxSidebarDrawer } from '../StorageBoxSidebarDrawerContext';
import StorageBoxShellSidebarResizeHandle from './StorageBoxShellSidebarResizeHandle';

function StorageBoxShellSidebarInner({ drawerOpenClass = '', ariaHidden }) {
  const closeDrawer = useStorageBoxSidebarDrawer();
  const sidebarColRef = useRef(null);
  const {
    readOnly,
    quota,
    parentId,
    goToRoot,
    writable,
    openCreateFolderModal,
    browseMode,
    openBin,
    openFavorites,
    vaultScope,
    lockVaultAccessActive,
    shellSidebarWidth,
    setShellSidebarWidth,
    persistShellSidebarWidth,
    commitShellSidebarWidth,
  } = useStorageBox();

  const [storageTermsOpen, setStorageTermsOpen] = useState(false);
  const inLockRealm = vaultScope === 'lock';
  const lockUnlocked = lockVaultAccessActive;
  const showVaultNavExtras = !inLockRealm || lockUnlocked;

  const usagePct =
    quota && !quota.unlimited && quota.limitBytes
      ? Math.min(100, (quota.usedBytes / quota.limitBytes) * 100)
      : 0;

  const openDriveFavorites = () => {
    if (inLockRealm) {
      goToRoot().then(() => openFavorites());
    } else {
      openFavorites();
    }
    closeDrawer();
  };

  const openDriveBin = () => {
    if (inLockRealm) {
      goToRoot().then(() => openBin());
    } else {
      openBin();
    }
    closeDrawer();
  };

  const openStorageTerms = () => {
    setStorageTermsOpen(true);
    closeDrawer();
  };

  useEffect(() => {
    const col = sidebarColRef.current;
    if (!col) return undefined;
    const layout = col.closest('.support-open-disc-shell-layout');
    if (!layout) return undefined;
    layout.style.setProperty('--sb-shell-sidebar-width', `${shellSidebarWidth}px`);
    return () => {
      layout.style.removeProperty('--sb-shell-sidebar-width');
    };
  }, [shellSidebarWidth]);

  const onResizeWidth = (nextWidth) => {
    setShellSidebarWidth(nextWidth);
    persistShellSidebarWidth();
  };

  return (
    <div
      ref={sidebarColRef}
      className={`support-open-disc-shell-sidebar-col sb-shell-sidebar-col sb-drive-sidebar-col sb-storagebox-shell-sidebar${drawerOpenClass}`}
      aria-label="Storage Box folders"
      aria-hidden={ariaHidden}
    >
      <div className="support-open-disc-shell-sidebar-col-inner sb-shell-sidebar sb-drive-sidebar">
        <div className="sb-drive-nav-group">
          <button
            type="button"
            className={`sb-drive-nav-item ${!inLockRealm && browseMode === 'drive' && !parentId ? 'sb-drive-nav-item--active' : ''}`}
            onClick={() => {
              goToRoot();
              closeDrawer();
            }}
          >
            <FiHardDrive aria-hidden />
            <span className="sb-drive-nav-item__label">{STORAGE_BOX_ROOT_LABEL}</span>
          </button>
          {!readOnly ? (
            <>
              {showVaultNavExtras ? (
                <>
                  <button
                    type="button"
                    className={`sb-drive-nav-item ${browseMode === 'favorites' ? 'sb-drive-nav-item--active' : ''}`}
                    onClick={() => {
                      if (inLockRealm) openFavorites();
                      else openDriveFavorites();
                      if (inLockRealm) closeDrawer();
                    }}
                  >
                    <FiStar aria-hidden />
                    <span className="sb-drive-nav-item__label">
                      {inLockRealm ? 'Lock favourites' : 'Favourites'}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`sb-drive-nav-item ${browseMode === 'bin' ? 'sb-drive-nav-item--active' : ''}`}
                    onClick={() => {
                      if (inLockRealm) openBin();
                      else openDriveBin();
                      if (inLockRealm) closeDrawer();
                    }}
                  >
                    <FiTrash2 aria-hidden />
                    <span className="sb-drive-nav-item__label">{inLockRealm ? 'Lock bin' : 'Bin'}</span>
                  </button>
                </>
              ) : null}
              <StorageBoxLockFolderNav />
            </>
          ) : null}
        </div>

        <StorageBoxSidebarShared />

        <StorageBoxSidebarColorTags />
        <StorageBoxSidebarLabels />

        {!inLockRealm ? (
          <div className="sb-drive-tree-section">
            <div className="sb-drive-tree-head">
              <p className="sb-drive-tree-label">Folders & files</p>
              {!readOnly && writable ? (
                <button
                  type="button"
                  className="sb-drive-tree-new-folder"
                  onClick={() => {
                    openCreateFolderModal();
                    closeDrawer();
                  }}
                  aria-label="New subfolder in current folder"
                  title="New subfolder in current folder"
                >
                  <FiPlus aria-hidden />
                </button>
              ) : null}
            </div>
            <ItemTree />
          </div>
        ) : null}

        <div className="sb-drive-sidebar-foot">
          {!readOnly && !quota ? <StorageBoxQuotaCardSkeleton /> : null}
          {!readOnly && quota ? (
            <div className="sb-quota-card sb-drive-quota">
              <h3>Storage</h3>
              {quota.unlimited ? (
                <p className="sb-quota-card__value">Unlimited</p>
              ) : (
                <>
                  <p className="sb-quota-card__value">
                    {formatBytes(quota.usedBytes)} of {formatBytes(quota.limitBytes)}
                  </p>
                  <div className="sb-quota-bar" aria-hidden>
                    <span style={{ width: `${usagePct}%` }} />
                  </div>
                </>
              )}
            </div>
          ) : null}
          <button
            type="button"
            className="sb-storage-terms-trigger"
            onClick={openStorageTerms}
            aria-haspopup="dialog"
            title="How Storage Box handles your files"
          >
            <FiInfo className="sb-storage-terms-trigger__icon" aria-hidden />
            <span className="sb-storage-terms-trigger__label">Storage terms &amp; help</span>
          </button>
        </div>
      </div>

      <StorageBoxStorageTermsModal isOpen={storageTermsOpen} onClose={() => setStorageTermsOpen(false)} />
      <StorageBoxShellSidebarResizeHandle
        widthPx={shellSidebarWidth}
        onWidthChange={onResizeWidth}
        onCommit={commitShellSidebarWidth}
      />
    </div>
  );
}

export default function StorageBoxShellSidebar({ drawerOpenClass = '', ariaHidden, onItemNavigate }) {
  return (
    <StorageBoxSidebarDrawerProvider onItemNavigate={onItemNavigate}>
      <StorageBoxShellSidebarInner drawerOpenClass={drawerOpenClass} ariaHidden={ariaHidden} />
    </StorageBoxSidebarDrawerProvider>
  );
}
